import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { InstantSchema } from '../primitives/time'

/**
 * La SUBIDA de documentos por dueño polimórfico (ADR 0022, capa 2).
 *
 * Es la puerta que faltaba. El eje de dueños existe desde la tajada 2 y el resolver de
 * herencia lo lee desde la tajada 6, pero **nada podía escribir en los cuatro niveles
 * de catálogo**: los endpoints legacy sólo aceptaban `:client` y `:negotiation`. El
 * modelo los conocía, el lector los resolvía, y no había forma de cargar un archivo.
 *
 * Un solo endpoint para los siete dueños en vez de siete endpoints: agregar un lugar
 * donde subir vuelve a ser lo que el ADR prometió —una entrada en el eje— y no un
 * controller nuevo.
 *
 * **La visibilidad NO viaja acá.** Todo nace `internal` y publicar es un acto aparte,
 * con su propio permiso (`PublishCatalogDocuments`). Si se pudiera publicar en la misma
 * llamada que sube, el permiso de publicación sería decorativo: cualquiera con permiso
 * de subir publicaría sin querer, y un publish en sociedad llega a cientos de teléfonos.
 */

export const V2DocumentOwnerTypeSchema = z.enum([
  'client',
  'negotiation',
  'portal-form-request',
  /** Expediente de crédito (checklist). Ver convención en `document-owner.ts`. */
  'credit',
  'society',
  'project',
  'type-property',
  'property'
])

export const V2UploadDocumentInputSchema = z.object({
  ownerType: V2DocumentOwnerTypeSchema,
  /**
   * Id del dueño. Id público (`prj_`, `prp_`, `typ_`, `soc_`) para los cuatro niveles
   * de catálogo; hex de ObjectId para cliente y negociación, que todavía no migraron.
   * El par se valida contra sí mismo antes de tocar la base.
   */
  ownerId: z.string().min(1, 'El dueño no tiene identificador.'),
  /** Nombre visible. Si falta, se usa el del archivo. */
  name: z.string().trim().min(1).max(200).optional(),
  comment: z.string().trim().max(1000).optional(),
  /** Carpeta contenedora (`fld_`), o ausente para la raíz del dueño. */
  folderId: PublicIdSchemas.folder.optional(),
  /**
   * La CATEGORÍA del cartapacio (`cat_`) bajo la que el cliente lo va a ver.
   *
   * Es el eje de agrupación de la galería, y es DISTINTO de `folderId`: la carpeta es el
   * escritorio de trabajo interno —árboles ad-hoc por cliente y negociación—, la categoría es la
   * plantilla global que se le muestra al cliente. Confundirlos es el error que dejó la galería
   * mostrando todo bajo «Otros documentos»: un `cat_` mandado como `folderId` no falla con un
   * error de tipo, devuelve `400 La carpeta indicada no existe.`
   *
   * Ausente = sin clasificar, que es como quedan las 1.030 filas previas. El cliente las ve
   * agrupadas bajo «Otros documentos», que es la verdad y no un error.
   */
  categoryId: PublicIdSchemas.documentCategory.optional(),
  /**
   * Clave del slot del checklist al que cuelga el archivo.
   *
   * En Créditos es `'<stageKey>.<itemKey>.<fieldKey>'` (SPEC-08). Ausente = adjunto
   * general del dueño, sin join a un campo. No inventa un endpoint: viaja por la misma
   * puerta de subida y se guarda en la fila de `attachments`.
   */
  requirementId: z.string().trim().min(1).max(200).optional()
})

export const V2UploadedDocumentSchema = z.object({
  /**
   * El `att_` del adjunto, NO su uuid interno. La distinción no es de estilo: el
   * interceptor de contrato v2 rechaza un uuid crudo en cualquier campo `id`, así que
   * mientras esta respuesta devolvió el uuid la subida fallaba con 500 DESPUÉS de
   * haber escrito el archivo y la fila.
   */
  id: PublicIdSchemas.attachment,
  name: z.string(),
  ownerType: V2DocumentOwnerTypeSchema,
  ownerId: z.string(),
  folderId: PublicIdSchemas.folder.nullable(),
  /** La categoría bajo la que el cliente lo va a ver, o `null` si quedó sin clasificar. */
  categoryId: PublicIdSchemas.documentCategory.nullable(),
  /** Asset de la biblioteca de medios que respalda el archivo (`ast_`). */
  assetId: z.string().nullable(),
  size: z.number(),
  mimeType: z.string().nullable(),
  /** Siempre `internal` recién subido. Publicar es otra acción y otro permiso. */
  visibility: z.enum(['public', 'internal'])
})

export type V2DocumentOwnerTypeInput = z.infer<typeof V2DocumentOwnerTypeSchema>
export type V2UploadDocumentInput = z.infer<typeof V2UploadDocumentInputSchema>
export type V2UploadedDocument = z.infer<typeof V2UploadedDocumentSchema>

/**
 * El acto de PUBLICAR, separado de la subida.
 *
 * Es un `PATCH` sobre un campo y no un `POST /publish` porque despublicar es la misma
 * operación con el otro valor: dos endpoints simétricos para un booleano son dos
 * lugares donde el permiso puede divergir.
 */
export const V2SetDocumentVisibilityInputSchema = z.object({
  visibility: z.enum(['public', 'internal'])
})

export const V2DocumentVisibilitySchema = z.object({
  id: PublicIdSchemas.attachment,
  visibility: z.enum(['public', 'internal'])
})

export type V2SetDocumentVisibilityInput = z.infer<typeof V2SetDocumentVisibilityInputSchema>

/**
 * Reclasificar un documento ya subido.
 *
 * Va aparte de la visibilidad aunque los dos escriban un campo de la misma fila: clasificar
 * ordena, publicar es lo que llega al teléfono de un cliente. Un solo endpoint con dos campos
 * opcionales dejaría que el permiso más flojo gobernara al más estricto.
 *
 * `null` es válido y significa «sacalo del grupo»: un documento mal puesto se corrige aunque
 * todavía no se sepa cuál es el suyo.
 */
export const V2SetDocumentCategoryInputSchema = z.object({
  categoryId: PublicIdSchemas.documentCategory.nullable()
})

export const V2DocumentCategoryResultSchema = z.object({
  id: PublicIdSchemas.attachment,
  categoryId: PublicIdSchemas.documentCategory.nullable()
})

export type V2SetDocumentCategoryInput = z.infer<typeof V2SetDocumentCategoryInputSchema>
export type V2DocumentCategoryResult = z.infer<typeof V2DocumentCategoryResultSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * SLOTS (ADR 0022, capa 2 mínima)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Qué PAPEL cumple un archivo en una pantalla de la app.
 *
 * Es otra cosa que la categoría, y la diferencia importa: la categoría la crea y renombra
 * el usuario para encontrar papeles; el slot lo define el producto y dice qué pinta la app.
 * Colgar la galería de una categoría bien conocida habría sido más barato hoy y frágil
 * mañana — la categoría es data editable, así que la primera persona que la renombre apaga
 * la galería de todos los clientes sin que nada falle ni avise.
 *
 * Un valor hoy. El punto no es tener muchos: es que «portada» o «render» sean una línea
 * acá y no una colección nueva.
 */
export const V2DocumentSlotSchema = z.enum(['gallery'])

/** `null` saca el documento del slot. */
export const V2SetDocumentSlotInputSchema = z.object({
  slot: V2DocumentSlotSchema.nullable()
})

export const V2DocumentSlotResultSchema = z.object({
  id: PublicIdSchemas.attachment,
  slot: V2DocumentSlotSchema.nullable()
})

/**
 * Reordenar un slot ENTERO.
 *
 * `attachmentIds` no es un parche: **es el slot**. Lo que no esté en la lista sale. Un
 * reorden que sólo agregara no podría expresar «saqué esta foto», que es la mitad de lo
 * que hace la pantalla.
 *
 * El primero es la PORTADA, y por eso el orden se escribe entero y de una vez: mover la
 * octava foto al principio reordena ocho filas, y hacerlo de a una deja estados
 * intermedios que un cliente puede ver en su teléfono.
 */
export const V2ReorderDocumentSlotInputSchema = z.object({
  ownerType: V2DocumentOwnerTypeSchema,
  ownerId: z.string().min(1),
  slot: V2DocumentSlotSchema,
  attachmentIds: z.array(PublicIdSchemas.attachment)
})

export const V2DocumentSlotOrderResultSchema = z.object({
  slot: V2DocumentSlotSchema,
  ids: z.array(PublicIdSchemas.attachment)
})

export type V2DocumentSlot = z.infer<typeof V2DocumentSlotSchema>
export type V2SetDocumentSlotInput = z.infer<typeof V2SetDocumentSlotInputSchema>
export type V2ReorderDocumentSlotInput = z.infer<typeof V2ReorderDocumentSlotInputSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * PREVISUALIZACIÓN
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * La URL firmada para VER una imagen del cartapacio — la miniatura, no el archivo.
 *
 * ## Por qué hace falta un endpoint y no alcanzaba con el de v1
 *
 * `GET /attachments/:id/url` existe desde siempre, pero direcciona por el **uuid legacy**, y
 * v2 no publica ese uuid: el listado devuelve el `att_` y el interceptor de contrato rechaza
 * un uuid crudo en cualquier campo `id`. O sea que la pestaña de galería tenía el id de cada
 * foto y ninguno de los dos servía para pedirla. Por eso mostraba nombres de archivo donde
 * el trabajo es mirar fotos.
 *
 * ## Por qué NO es «la descarga» con otro nombre
 *
 * Dos decisiones que lo separan de `getDownloadUrl`, y las dos son deliberadas:
 *
 * 1. **Sólo firma IMÁGENES.** No es un detalle de UI: el endpoint de descarga deja registro
 *    en la bitácora de adjuntos y éste no —ver abajo—, así que si además sirviera PDFs sería
 *    exactamente el camino para bajarse un documento sin dejar rastro. La restricción es lo
 *    que sostiene la excepción de auditoría, no una comodidad.
 * 2. **No audita.** Abrir la pestaña pide las doce miniaturas de una; auditarlas escribiría
 *    doce «descargas» por cada vistazo y en un mes la bitácora no distinguiría a quien se
 *    llevó un archivo de quien pasó por la pantalla. Mirar la miniatura de una foto que uno
 *    mismo está curando no es el hecho que esa bitácora existe para registrar.
 */
export const V2DocumentPreviewUrlSchema = z.object({
  id: PublicIdSchemas.attachment,
  /** Firmada y temporal. No se guarda ni se comparte: se vuelve a pedir. */
  url: z.string().url(),
  /**
   * Vigencia en segundos. Viaja porque el consumidor la necesita para saber cuánto puede
   * cachear: una miniatura pegada en caché más de lo que dura la firma es una imagen rota
   * que aparece sola, sin que nada haya cambiado.
   */
  expiresInSeconds: z.number().int().positive()
})

export type V2DocumentPreviewUrl = z.infer<typeof V2DocumentPreviewUrlSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * DESCARGA / VISUALIZACIÓN
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * La URL firmada para ABRIR o BAJAR el archivo, sea cual sea su tipo.
 *
 * ## Por qué existe si ya está `V2DocumentPreviewUrlSchema`
 *
 * Porque la previsualización **sólo firma imágenes**, y esa restricción no es cosmética: es lo
 * que sostiene que no deje registro en la bitácora. El expediente de un crédito son PDFs —cartas
 * de autorización, solvencias, facturas—, así que por esa puerta un adjunto de crédito recibía
 * `400` y no había ninguna otra: `GET /attachments/:id/url` de v1 direcciona por el **uuid
 * legacy**, y v2 nunca publica ese uuid. Un archivo que se podía subir y no se podía volver a
 * mirar.
 *
 * ## Qué la hace la descarga y no otra previsualización
 *
 * **Audita.** Cada emisión escribe un `DOWNLOAD` en la bitácora de adjuntos, con quién y cuándo
 * — el mismo hecho que registra la puerta de v1. Por eso puede servir cualquier MIME: el rastro
 * es lo que se compra con el permiso de servir un PDF.
 *
 * ## `disposition` lo decide el SERVIDOR
 *
 * Ver y descargar son la **misma llamada** con distinto `Content-Disposition`, derivado del MIME
 * (`ContentTypesAllowedView`): un PDF se abre `inline` en la pestaña, un `.docx` se ofrece para
 * guardar. La UI no elige — si eligiera, dos pantallas terminarían decidiendo distinto sobre el
 * mismo archivo. Lo que la UI hace con el resultado es siempre lo mismo: abrirlo.
 */
export const V2DocumentDownloadUrlSchema = z.object({
  id: PublicIdSchemas.attachment,
  /** El nombre VISIBLE, tal como se curó. Puede no tener extensión. */
  name: z.string(),
  /**
   * El nombre que se le sugiere al sistema operativo al guardar, extensión incluida.
   *
   * Sale de la KEY de storage y no del nombre visible: el curador pudo renombrar el archivo a
   * algo sin punto, y ahí el navegador se queda sin saber qué acaba de bajar.
   */
  fileName: z.string(),
  mimeType: z.string().nullable(),
  size: z.number().int().nonnegative(),
  /** Firmada y temporal. No se guarda ni se comparte: se vuelve a pedir. */
  url: z.string().url(),
  /** Vigencia en segundos, para que el consumidor derive de acá cuánto puede cachear. */
  expiresInSeconds: z.number().int().positive()
})

export type V2DocumentDownloadUrl = z.infer<typeof V2DocumentDownloadUrlSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * RENOMBRAR
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Cambiar el nombre VISIBLE de un documento ya subido.
 *
 * ## Por qué es un endpoint y no «volvé a subirlo»
 *
 * Porque el nombre es lo único que se equivoca sin que el archivo esté mal. Corregir un
 * `escaneo_final_v3(1).pdf` borrando y volviendo a subir cuesta la bitácora del adjunto, su
 * `assetId` y —en el checklist de un crédito— la referencia del campo que ya lo señala. v1 ya lo
 * resolvía con `PATCH /attachments/:id`, pero direcciona por el uuid legacy que v2 no publica.
 *
 * ## Sólo el nombre
 *
 * No lleva `comment`, ni `folderId`, ni `categoryId`: cada uno de esos ya tiene su puerta con su
 * permiso, y un `PATCH` con seis campos opcionales es el lugar donde el permiso más flojo
 * termina gobernando al más estricto. El nombre no cambia quién ve el archivo.
 *
 * El límite de 200 es el MISMO que el de la subida (`V2UploadDocumentInputSchema.name`): un
 * nombre que se pudo cargar tiene que poder corregirse.
 */
export const V2RenameDocumentInputSchema = z.object({
  name: z.string().trim().min(1, 'El nombre no puede quedar vacío.').max(200)
})

export const V2DocumentNameResultSchema = z.object({
  id: PublicIdSchemas.attachment,
  name: z.string()
})

export type V2RenameDocumentInput = z.infer<typeof V2RenameDocumentInputSchema>
export type V2DocumentNameResult = z.infer<typeof V2DocumentNameResultSchema>

/** Una fila de la pestaña de curaduría: lo que ESE dueño tiene subido. */
export const V2OwnedDocumentSchema = z.object({
  id: PublicIdSchemas.attachment,
  name: z.string(),
  comment: z.string().optional(),
  folderId: PublicIdSchemas.folder.nullable(),
  /** La categoría bajo la que el cliente lo ve. La pestaña de curaduría la muestra y la edita. */
  categoryId: PublicIdSchemas.documentCategory.nullable(),
  assetId: z.string().nullable(),
  size: z.number(),
  mimeType: z.string().nullable(),
  visibility: z.enum(['public', 'internal']),
  /**
   * `InstantSchema` y no `z.coerce.date()`: la coerción no declara ningún tipo en el
   * JSON Schema, así que el contrato publicaba un campo SIN tipo y el SDK generado lo
   * tipaba `unknown` — que es precisamente lo que el gate de OpenAPI prohíbe. En el
   * cable siempre fue un ISO-8601 en UTC; ahora el contrato lo dice.
   */
  createdAt: InstantSchema.nullable(),
  /**
   * En qué SLOT está, si está en alguno. Viaja en el listado porque es lo que permite que
   * la pestaña de galería y la del cartapacio se sirvan de UNA sola consulta: son dos
   * vistas del mismo conjunto, no dos conjuntos.
   */
  slot: V2DocumentSlotSchema.nullable(),
  /** Posición dentro del slot. La primera es la portada. `null` fuera de todo slot. */
  slotOrder: z.number().int().nonnegative().nullable(),
  /**
   * Clave del requisito/campo al que cuelga el archivo, o `null` si es un adjunto general.
   * En Créditos: `'<stageKey>.<itemKey>.<fieldKey>'` (SPEC-08).
   */
  requirementId: z.string().nullable(),
  /**
   * Si el asset tiene una miniatura GENERADA — la hoja en WebP de
   * `generate-document-thumbnails.use-case.ts`, no el original.
   *
   * ## Por qué viaja en el listado y la URL no
   *
   * Porque son dos datos con dos vidas. «Tiene o no tiene» es estable: cambia cuando se
   * genera la preview y nunca más. La URL firmada vence a los quince minutos, así que meterla
   * acá ataría su vigencia a la de la lista — cualquier mutación que refresque el listado sin
   * refrescar las firmas dejaría miniaturas rotas, y una lista de cuarenta cargaría cuarenta
   * firmas de las que se miran ocho.
   *
   * Con esta bandera la fila sabe, sin pedir nada, si le corresponde una miniatura o el arte
   * por tipo de archivo. Sólo las que sí piden su URL, y cada una se renueva cuando le toca.
   */
  hasThumbnail: z.boolean(),
  /**
   * La FORMA de la hoja (ancho/alto), para reservar la caja antes de que la imagen cargue.
   *
   * `null` cuando no hay miniatura o cuando el derivado es anterior a que se guardaran las
   * medidas. Quien lea cae a carta, que es lo que la tarjeta hace para todo lo que no la declara.
   */
  thumbnailAspect: z.number().positive().nullable(),
  /**
   * Páginas del documento, para poder decir «Documento · 3 páginas» sin abrirlo.
   *
   * Es el MISMO dato que el contrato del cliente ya publica (`V2ClientDocumentSchema.pages`), y
   * viaja acá por la misma razón: la tarjeta del cartapacio es la misma tarjeta. `null` en lo
   * que no es PDF y en las filas anteriores al modelo de medios; la tarjeta dice «Documento» a
   * secas, que es lo que se sabe, en vez de inventar un número.
   */
  pages: z.number().int().positive().nullable()
})

/**
 * Sin paginación: es la pestaña de UN dueño y el volumen real son unidades o decenas
 * (el máximo medido, 22). Paginar esto sería vestir de cursor algo que nunca pagina.
 */
export const V2OwnedDocumentsSchema = z.object({
  data: z.array(V2OwnedDocumentSchema)
})

export type V2OwnedDocument = z.infer<typeof V2OwnedDocumentSchema>
export type V2OwnedDocuments = z.infer<typeof V2OwnedDocumentsSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * REUSO DE LA GALERÍA ENTRE DUEÑOS
 * ──────────────────────────────────────────────────────────────────────────── */

/** Un dueño del eje, nombrado entero. El par se valida contra sí mismo en el servidor. */
export const V2GalleryOwnerRefSchema = z.object({
  ownerType: V2DocumentOwnerTypeSchema,
  ownerId: z.string().min(1, 'El dueño no tiene identificador.')
})

export type V2GalleryOwnerRef = z.infer<typeof V2GalleryOwnerRefSchema>

/**
 * Qué hace la copia con lo que el destino YA tiene en la galería.
 *
 * - `append` — las fotos del origen se suman al final. Es el modo por defecto de la pantalla.
 * - `replace` — las propias del destino SALEN DEL SLOT antes de sumar las del origen. Salen, no
 *   se borran: el archivo queda en el cartapacio y se puede volver a poner. Borrarlas sería
 *   destruir trabajo de curaduría para conseguir un orden, y el orden se consigue igual.
 */
export const V2CopyGalleryModeSchema = z.enum(['append', 'replace'])

export type V2CopyGalleryMode = z.infer<typeof V2CopyGalleryModeSchema>

/**
 * COPIAR LA GALERÍA DE UN DUEÑO A OTRO, sin mover un solo byte.
 *
 * ## Por qué existe
 *
 * Muchos subtipos comparten las mismas fotos de carrusel —el mismo render, la misma fachada— y
 * hasta ahora la única forma de tenerlas en los dos era **subir el archivo dos veces**. El dedupe
 * de assets no ayudaba: es por `(checksum, storageKey)` y la key lleva el dueño adentro, así que
 * el mismo JPG subido a dos niveles son dos objetos en el bucket y dos filas en `assets`.
 *
 * ## Qué hace exactamente
 *
 * Crea `Attachment` NUEVOS en el destino apuntando al **mismo `assetId` y al mismo `path`** que
 * los del origen. Es el patrón que el ADR 0022 ya declaró doctrina y que
 * `SyncApprovalProjectionUseCase` viene usando desde la proyección de flujos: un asset, muchos
 * usos, cero copias.
 *
 * ## Por qué NO es herencia
 *
 * Porque la herencia ya existe y resuelve otro problema: un inmueble VE las fotos de su proyecto
 * sin copiarlas. Esto es para dos dueños que **no están en la misma cadena** —dos subtipos
 * hermanos, un subtipo y el proyecto de otra sociedad— donde no hay herencia que los una. Y es
 * para el caso en que el curador quiere las fotos del proyecto **como propias**, para reordenarlas
 * o despublicar algunas sin tocar el origen.
 *
 * ## Permiso
 *
 * El de escribir en el DESTINO (`ManageCatalogDocuments` para los cuatro niveles de catálogo), más
 * el de LEER el origen — que es el mismo permiso, resuelto sobre el otro nivel. Publicar no se
 * hereda de la copia: lo copiado nace con la visibilidad que tenía en el origen sólo si quien
 * copia puede publicar en el destino; si no, entra interno. Ver el docblock del caso de uso.
 */
export const V2CopyGalleryInputSchema = z.object({
  from: V2GalleryOwnerRefSchema,
  to: V2GalleryOwnerRefSchema,
  mode: V2CopyGalleryModeSchema
})

export const V2CopyGalleryResultSchema = z.object({
  to: V2GalleryOwnerRefSchema,
  mode: V2CopyGalleryModeSchema,
  /** Cuántas filas nuevas se crearon en el destino. */
  copied: z.number().int().nonnegative(),
  /**
   * Cuántas NO generaron fila nueva porque el destino ya tenía ese mismo archivo.
   *
   * Se REUSA la fila que ya existía —volviéndola a poner en el slot— en vez de fabricar una
   * gemela. Copiar dos veces desde el mismo origen es un gesto normal (se agregó una foto allá y
   * se quiere acá) y la segunda pasada no puede dejar el carrusel con todo repetido.
   */
  skipped: z.number().int().nonnegative(),
  /** Cuántas propias del destino salieron del slot. Sólo en `replace`; el archivo no se borra. */
  detached: z.number().int().nonnegative(),
  /**
   * Cuántas NO entraron por el tope del slot.
   *
   * Es lo que la pantalla necesita para no mentir: «se copiaron 12 de 15» es una frase distinta de
   * «se copiaron 15».
   */
  skippedByLimit: z.number().int().nonnegative(),
  /** Los `att_` creados, en el orden en que quedaron en la galería. */
  ids: z.array(PublicIdSchemas.attachment)
})

export type V2CopyGalleryInput = z.infer<typeof V2CopyGalleryInputSchema>
export type V2CopyGalleryResult = z.infer<typeof V2CopyGalleryResultSchema>

/**
 * De dónde se puede copiar: los candidatos que el diálogo «Copiar desde…» lista.
 *
 * Son dos familias y la distinción viaja en `relation` porque la pantalla las agrupa distinto:
 *
 * - `chain` — los dueños por encima en la cadena de herencia (el subtipo, el proyecto, la
 *   sociedad). Copiar de acá es **materializar lo heredado**: el destino pasa a tener como
 *   propias unas fotos que ya veía, para poder reordenarlas o quitar alguna.
 * - `sibling` — los otros subtipos del MISMO proyecto. Acá no hay herencia que los una, y es el
 *   caso que el negocio pidió: «algunos subtipos tendrán las mismas imágenes del carrusel».
 *
 * `photoCount` y `coverDocumentId` viajan para que la lista se pueda leer sin abrir cada uno: un
 * candidato con cero fotos se ve deshabilitado en vez de ofrecer una copia vacía, y la miniatura
 * de la portada se pide con `GET /admin/documents/:id/url`, que ya existe.
 */
export const V2GallerySourceSchema = z.object({
  ownerType: V2DocumentOwnerTypeSchema,
  ownerId: z.string(),
  /** Cómo se lo nombra. En un subtipo es el CÓDIGO: hay ocho llamados «APARTAMENTO» por proyecto. */
  label: z.string(),
  relation: z.enum(['chain', 'sibling']),
  photoCount: z.number().int().nonnegative(),
  /** La primera foto de ese dueño, para pedirle la miniatura. `null` si no tiene ninguna. */
  coverDocumentId: PublicIdSchemas.attachment.nullable()
})

export const V2GallerySourcesSchema = z.object({
  data: z.array(V2GallerySourceSchema)
})

export type V2GallerySource = z.infer<typeof V2GallerySourceSchema>
export type V2GallerySources = z.infer<typeof V2GallerySourcesSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * MINIATURA GENERADA
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * La URL firmada de la MINIATURA de un documento — el derivado, nunca el original.
 *
 * ## Por qué no alcanzaba con `V2DocumentPreviewUrlSchema`
 *
 * Aquélla firma **el archivo original y sólo si es una imagen**. Las dos mitades hacían daño:
 *
 * - *El original.* Las fotos reales del catálogo son de 8750 × 4063 —35 megapíxeles, ~142 MB de
 *   bitmap— y se pintaban en una celda de 150 px. El navegador se queda con esos megapíxeles en
 *   memoria por cada foto de la grilla; `decoding="async"` lo mitigaba y no lo arreglaba. El
 *   arreglo de fondo siempre fue servir la hoja generada, que ya existe: 768 × 1024 en WebP.
 * - *Sólo imágenes.* El cartapacio es PDFs. Por esa puerta un PDF es `400`, así que la lista de
 *   papeles no podía tener miniatura de nada — y una lista de documentos sin miniaturas es una
 *   lista de nombres de archivo, que es exactamente lo que había.
 *
 * ## Por qué ésta SÍ puede servir un PDF, y sigue sin auditar
 *
 * Porque no sirve el PDF: sirve **el derivado**, y el derivado tiene un techo que el generador
 * ya fijó — dos páginas, 768 × 1024 (`DOCUMENT_THUMBNAIL_MAX_PAGES`). No es una puerta por la
 * que se pueda sacar un documento sin dejar rastro, que es lo que la restricción de
 * `getPreviewUrl` protegía; es la misma hoja que la app del cliente ya recibe de lo publicado.
 * La descarga del archivo entero sigue siendo `GET :id/download-url`, y ésa sí audita.
 *
 * No audita por la misma razón que la previsualización: abrir la pestaña pide veinte de una, y
 * veinte «descargas» por vistazo vuelven inútil la bitácora que distingue a quien se llevó un
 * archivo de quien pasó por la pantalla.
 *
 * ## `url` es nullable y eso NO es un error
 *
 * Un asset sin preview —un `.docx`, un PDF cifrado que PDF.js no pudo abrir, una fila anterior
 * al generador— responde `200` con `url: null`. Un `404` obligaría a cada consumidor a tratar
 * «no tiene» como una falla y a reintentarla; acá es una respuesta, y la fila pinta el arte por
 * tipo de archivo sin más viajes.
 */
export const V2DocumentThumbnailUrlSchema = z.object({
  id: PublicIdSchemas.attachment,
  /**
   * Las hojas firmadas, en orden, o **vacío** si este documento no tiene miniatura generada.
   *
   * Un arreglo y no una sola URL porque la tarjeta dibuja una PILA: dos hojas rotadas con el
   * abanico que el dominio calcula (`backPageLayout`), que es la señal de que el documento tiene
   * más de una página. Con una sola URL la pila se aplana y todos los documentos se ven iguales.
   * Son las mismas dos que recibe la app del cliente, por el mismo contrato de nombres.
   *
   * Vacío y no `null`: quien consuma recorre igual, y una lista vacía no necesita una rama.
   */
  urls: z.array(z.string().url()),
  /** Ancho/alto de la hoja, para reservar la caja. `null` en los derivados sin medidas. */
  aspect: z.number().positive().nullable(),
  /** Vigencia en segundos, para que el consumidor derive cuánto puede cachear. */
  expiresInSeconds: z.number().int().positive()
})

export type V2DocumentThumbnailUrl = z.infer<typeof V2DocumentThumbnailUrlSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * HEREDADOS
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Lo que un nivel de catálogo HEREDA de sus ancestros: lo publicado más arriba en la cadena.
 *
 * ## El hueco que cierra
 *
 * `GET /v2/admin/documents` devuelve lo que un dueño POSEE, y eso está bien: mezclar lo
 * heredado en la misma lista invitaría a «borrar» desde un proyecto un plano que vive en la
 * sociedad. Pero la consecuencia no intencionada era que el curador de un inmueble **no tenía
 * ninguna forma de ver lo que su cliente sí ve**: abría la pestaña, encontraba dos documentos y
 * concluía que el expediente estaba casi vacío mientras el teléfono del cliente mostraba nueve.
 *
 * Esta lectura es la otra mitad, y es de SÓLO LECTURA a propósito. El ADR 0023 ya decidió la
 * escalera: ver el árbol resuelto no pide permiso —«Ver NO es un permiso»— y editar el nodo de
 * origen pide `ManageCatalogDocuments` en ese origen. Dos listas separadas enseñan esa
 * distinción sin una línea de ayuda; una sola lista con banderitas la borra.
 *
 * ## Sólo lo PÚBLICO, y sólo de los ANCESTROS
 *
 * Un heredado interno no lo ve nadie aguas abajo: listarlo sería mostrar el escritorio de otro
 * equipo. Y el dueño que se está mirando queda fuera de su propia herencia — eso ya está en la
 * lista de arriba, y duplicarlo haría que cada documento propio se viera dos veces.
 */
export const V2InheritedDocumentSchema = z.object({
  id: PublicIdSchemas.attachment,
  name: z.string(),
  mimeType: z.string().nullable(),
  size: z.number().nullable(),
  categoryId: PublicIdSchemas.documentCategory.nullable(),
  /**
   * De qué NIVEL viene. Nunca es el dueño consultado.
   *
   * Viaja el nivel y no el nombre de la entidad, y no por ahorrar: en una cadena hay a lo sumo
   * un proyecto y una sociedad por encima, así que el nombre no desambigua nada —confirma— y a
   * cambio costaría una consulta por nivel en cada apertura de la pestaña. Lo que el curador
   * necesita saber es a QUIÉN ir a pedírselo, y eso es el nivel.
   */
  originType: z.enum(['society', 'project', 'type-property', 'property']),
  hasThumbnail: z.boolean(),
  thumbnailAspect: z.number().positive().nullable(),
  /** Páginas del documento, para que la tarjeta heredada diga lo mismo que la propia. */
  pages: z.number().int().positive().nullable()
})

/** Sin paginación, por el mismo motivo que el listado propio: son unidades o decenas. */
export const V2InheritedDocumentsSchema = z.object({
  data: z.array(V2InheritedDocumentSchema)
})

export type V2InheritedDocument = z.infer<typeof V2InheritedDocumentSchema>
export type V2InheritedDocuments = z.infer<typeof V2InheritedDocumentsSchema>
